/**
 * ExtensionStatus — live detection UI for the Keep Me Out browser extension (adr/0005 client).
 *
 * `useExtension()` runs the postMessage handshake; `ExtensionBadge` is the compact "connected" pill
 * (safe to drop anywhere — renders nothing until the extension answers); `ExtensionDownloads` is the
 * per-browser install grid for the /extension page. Self-contained inline styles so it renders the same
 * inside the marketing chrome, the portal ComingSoon gate, or the dashboard.
 */
import React from 'react';
import { useExtension, detectBrowser, STORE_LINKS } from '../app/data/extension';

// Re-export so existing importers (views/Extension.js) keep resolving it from here. The hook now lives
// in the portal data module (src/app/data/extension) so the marketing page and the portal share one
// implementation.
export { useExtension };

const BROWSER_LABEL = {
  chrome: 'Chrome',
  brave: 'Brave',
  edge: 'Edge',
  opera: 'Opera',
  firefox: 'Firefox',
  safari: 'Safari',
};

export function ExtensionBadge({ style }) {
  const { present, info, today } = useExtension();
  if (!present) return null;
  const tried = today ? today.attempts.adult + today.attempts.custom : null;
  return (
    <div
      role="status"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        padding: '8px 14px',
        borderRadius: 999,
        background: 'rgba(52,211,153,.12)',
        border: '1px solid rgba(52,211,153,.4)',
        color: '#86efac',
        fontSize: 13,
        fontWeight: 600,
        ...style,
      }}
    >
      <span aria-hidden="true">✓</span>
      Browser extension connected ({BROWSER_LABEL[info.browser] || info.browser})
      {tried != null && (
        <span style={{ color: '#cbd5e1', fontWeight: 500 }}>· {tried} tried, {today.blocked} blocked today</span>
      )}
    </div>
  );
}

export function ExtensionDownloads() {
  const current = detectBrowser();
  const order = [current, ...Object.keys(STORE_LINKS).filter((b) => b !== current)];
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
        gap: 12,
        margin: '8px 0 4px',
      }}
    >
      {order.map((b) => {
        const s = STORE_LINKS[b];
        const isCurrent = b === current;
        const available = !!s.url;
        return (
          <a
            key={b}
            href={available ? s.url : undefined}
            target={available ? '_blank' : undefined}
            rel="noreferrer"
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
              padding: '16px 14px',
              borderRadius: 14,
              textDecoration: 'none',
              background: isCurrent ? 'rgba(110,168,254,.14)' : 'rgba(255,255,255,.04)',
              border: `1px solid ${isCurrent ? 'rgba(110,168,254,.5)' : 'rgba(255,255,255,.10)'}`,
              color: '#eef2ff',
              cursor: available ? 'pointer' : 'default',
              opacity: available ? 1 : 0.72,
            }}
          >
            <span style={{ fontSize: 22 }} aria-hidden="true">{s.icon}</span>
            <span style={{ fontWeight: 700 }}>
              {s.label}
              {isCurrent && <span style={{ color: '#6ea8fe', fontSize: 12, fontWeight: 600 }}> · your browser</span>}
            </span>
            <span style={{ fontSize: 12, color: '#8b93b7' }}>{available ? s.store : 'Coming soon'}</span>
          </a>
        );
      })}
    </div>
  );
}
